import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { navigateToEditor, navigateToObject } from 'router/navigation';

import { setAdvancedFilters, updateRequest } from 'Search/redux/SearchPageSlice';

import { FormattedDate, UserCard } from '_common/components';

import styles from '../SearchPage.module.scss';
import MetadataCell from './MetadataCell';
import AuthorsCell from './AuthorsCell';
import ReferencePriorityCell from './ReferencePriorityCell';
import { setObjectPreview } from '_common/components/ObjectPreview/ObjectPreviewSlice';
import { useGetUserSettingsQuery } from '_common/services/api/UserApi';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';

import { Table as BaseTable } from '_common/components/Table';
import { TableColumn, TableRow, onDoubleClickHandler, Cell } from '_common/components/Table';

type TableProps = {
  searchQuery: string;
};

type RowData = Objekt;

const Table = ({ searchQuery }: TableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: metadata } = useGetMetadataListQuery();
  const data = useSelector((state) => state.app.data);
  const list = useSelector((state) => state.table.identity.search.list);

  const params = useSelector((state) => state.search.request.params);
  const isNextPageLoading = useSelector((state) => state.search.request.isNextPageLoading);
  const advancedFilters = useSelector((state) => state.search.advancedFilters);
  const advancedFiltersArray = useMemo(() => Object.values(advancedFilters), [advancedFilters]);
  const extraColumnsValues: string[] = [];

  const handleExtraColumns = () => {
    if (searchQuery) {
      ['deadline', 'authors.name'].forEach((column) => {
        if (searchQuery.includes(column) && !extraColumnsValues.includes(column)) {
          extraColumnsValues.unshift(column === 'deadline' ? 'due' : column);
        }
      });
      if (metadata) {
        const metadataIds = Object.keys(metadata);
        metadataIds.forEach((metadataId) => {
          const name = `metadata.${metadata[metadataId].name.toLowerCase().replace(/ /, '_')}`;
          if (searchQuery.includes(name) && !extraColumnsValues.includes(name)) {
            extraColumnsValues.unshift(`metadata.${metadataId}`);
          }
        });
      }
      if (advancedFilters) {
        const filter = advancedFiltersArray.filter(
          (filter: { option: string }) => filter.option === 'reference_priority',
        );
        if (filter.length > 0) {
          filter.forEach((element) => {
            const name = `${element.option}.${element.value}.${element.auxValue}`;
            if (!extraColumnsValues.includes(name)) {
              extraColumnsValues.push(name);
            }
          });
        }
      }
    }
  };

  handleExtraColumns();

  const commonColumns = useMemo<TableColumn<RowData, keyof TableCustomisation>[]>(() => {
    const columns: TableColumn<RowData, keyof TableCustomisation>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;

          return (
            <Cell testId={`${object.id}-name-column`}>
              <Cell.ObjectName object={object} testId={`${object.id}-object-name`} />
            </Cell>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'storage.browserHeader.status' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-status-column`}>
              <Cell.ObjectStatus object={object} testId="object-status" />
            </Cell>
          );
        },
      },
      {
        id: 'time.modification',
        accessorKey: 'time.modification',
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-date-modified-column`}>
              <Cell.DateTime
                date={{ date: object.time.modification }}
                time={{ time: object.time.modification }}
              />
            </Cell>
          );
        },
      },
      {
        id: 'location',
        enableSorting: true,
        accessorKey: 'location',
        size: 160,
        header: intl.formatMessage({ id: 'LOCATION' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-location-column`}>
              <Cell.Location object={object} />
            </Cell>
          );
        },
      },
      {
        id: 'shared_with',
        accessorKey: 'shared_with',
        size: 120,
        header: intl.formatMessage({ id: 'storage.browserHeader.sharedWith' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-shared-column`}>
              <Cell.Collaborators object={object} />
            </Cell>
          );
        },
      },
      {
        id: 'owner',
        accessorKey: 'owner',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({ id: 'global.owner' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-owner-column`}>
              <UserCard userId={object.owner} />
            </Cell>
          );
        },
      },
      {
        id: 'time.creation',
        accessorKey: 'time.creation',
        header: intl.formatMessage({ id: 'CREATION_DATE' }),
        size: 160,
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-creation-date-column`}>
              <Cell.DateTime
                date={{ date: object.time.creation }}
                time={{ time: object.time.creation }}
              />
            </Cell>
          );
        },
      },
    ];

    return columns.filter((column) => userSettings?.tables.search[column.id]);
  }, [userSettings, extraColumnsValues, data]);

  const extraColumns = useMemo<TableColumn<RowData>[]>(() => {
    if (extraColumnsValues.length > 0) {
      return extraColumnsValues.map((extra: string) => {
        const columnType = extra.split('.');

        switch (columnType[0]) {
          case 'due':
            return {
              id: 'events.due',
              accessorKey: 'events.due',
              enableSorting: false,
              size: 120,
              header: intl.formatMessage({ id: 'settings.general.dueDate' }),
              cell: (info) => {
                const object = info.row.original;
                return (
                  <Cell testId={`${object.id}-due-date-column`}>
                    <FormattedDate date={object.events.due} />
                  </Cell>
                );
              },
            };
          case 'authors':
            return {
              id: 'authors',
              accessorKey: 'authors',
              size: 200,
              header: intl.formatMessage({ id: 'EDITOR_DOCUMENT_AUTHORS' }),
              cell: (info) => {
                const object = info.row.original;
                return (
                  <Cell testId={`${object.id}-authors-column`}>
                    <AuthorsCell object={object} />
                  </Cell>
                );
              },
            };
          case 'metadata': {
            const metadataId = columnType[1];
            const values = metadata?.[metadataId];
            const name = values?.name.replaceAll(' ', '_').toLowerCase();
            return {
              id: name ?? 'metadata',
              accessorKey: name ?? 'metadata',
              size: 200,
              enableSorting: false,
              header: values?.name ?? 'metadata',
              cell: (info) => {
                const object = info.row.original;
                return (
                  <div data-testid={`${object.id}-${name}-metadata-column`}>
                    <MetadataCell object={object} metadata={values} />
                  </div>
                );
              },
            };
          }
          case 'reference_priority': {
            const priority = columnType[1];
            const inDocument =
              !columnType[2] || columnType[2] === 'undefined' ? 'not' : columnType[2];
            return {
              id: extra,
              accessorKey: extra,
              size: 160,
              header: intl.formatMessage({
                id: inDocument === 'is' ? 'REFERENCE_IN_DOCUMENT' : 'REFERENCE_IN_LIBRARY',
              }),
              cell: (info) => {
                const object = info.row.original;
                const inDocumentTestId = inDocument === 'is' ? 'in-document' : 'in-library';

                return (
                  <Cell
                    testId={`${object.id}-${priority}-reference-priority-${inDocumentTestId}-column`}
                  >
                    <ReferencePriorityCell object={object} value={`${priority}.${inDocument}`} />
                  </Cell>
                );
              },
            };
          }
          default:
            return {
              id: 'null',
            };
        }
      });
    }
    return [];
  }, [extraColumnsValues]);

  const columns = () => {
    if (extraColumnsValues.length > 0) {
      return [...commonColumns, ...extraColumns];
    } else {
      return commonColumns;
    }
  };

  const rowsData = useMemo(() => {
    const rowsData = list?.map<TableRow<RowData>>((objectId) => {
      return data[objectId];
    });
    return rowsData;
  }, [list, data]);

  const handleOrderUpdate = (newOrder: Request.OrderParams) => {
    dispatch(
      updateRequest({
        params: { ...params, order_field: newOrder.order_field, order_type: newOrder.order_type },
      }),
    );
  };

  //@ts-expect-error
  const handleRowDoubleClick: onDoubleClickHandler = (row, originalEvent) => {
    const currentObject = data[row.id];
    if (currentObject.status === 'processing' || currentObject.status === 'broken') {
      return;
    }

    const type = currentObject.type;
    switch (type) {
      case 'document':
        navigateToEditor(currentObject.id);
        break;
      case 'file':
        dispatch(setObjectPreview({ id: currentObject.id }));
        originalEvent.preventDefault();
        break;
      case 'folder':
        navigateToObject('folder', currentObject.id);
        break;
      case 'dopdf':
        window.open(`/pdf/${currentObject.id}`, '_blank');
        break;
      case 'presentation':
        window.open(`/presentation/${currentObject.id}`, '_blank');
        break;
      default:
        break;
    }
  };

  const handleOnClickRemoveAllFilters = () => {
    dispatch(setAdvancedFilters({}));
  };

  const renderEmptyView = () => {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', flex: 1 }}
        data-testid="search-empty"
      >
        <EmptyState
          size="l"
          icon="magnifying-glass"
          title={intl.formatMessage({ id: 'SEARCH_EMPTY_TITLE' })}
          cta={{
            onClick: handleOnClickRemoveAllFilters,
            children: intl.formatMessage({ id: 'CLEAR_FILTERS' }),
          }}
          description={intl.formatMessage({ id: 'SEARCH_EMPTY_DESCRIPTION' })}
          testId="search-page"
        />
      </div>
    );
  };

  return (
    <div className={styles.table} data-testid="search-table">
      <BaseTable
        identity="search"
        selectRowOnClick
        options={{ data: rowsData, columns: columns() }}
        renderFooter={() => null}
        isLoading={isNextPageLoading}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        testId="search"
        renderEmptyState={renderEmptyView}
        onDoubleClick={handleRowDoubleClick}
        onOrder={handleOrderUpdate}
        order={{ order_field: params.order_field, order_type: params.order_type }}
        columnPinning={{ left: ['name'] }}
      />
    </div>
  );
};

export default Table;
